/**
 * "Open with the client" — `mockups/studio/client-detail.html` §Open items.
 *
 * The mockup's own comment: "the only ochre panel on the page." DESIGN.md P4 is why
 * that sentence is in the markup — ochre means *this needs a decision* — so nothing
 * else on this page is `card--need` and nothing here is anything else.
 *
 * **Two arrays, one panel.** What sits with the client is `decisions` (a quote, an
 * extra, a package change, a content plan — `OpenDecisionSchema`'s own four) plus the
 * `blocked_on_client` open items; what is ours is the `ours_to_quote` ones. The
 * mockup's head note — "Three sit with them; one is ours to send" — is that split,
 * counted rather than typed.
 *
 * Only a decision gets a **Nudge**, and `NudgeButton`'s header says why: the mail it
 * sends needs a reference, an amount and two dates, and an open item carries none of
 * them.
 *
 * **A quote we owe is late, says so, and has somewhere to go** — session 24, R2-30.
 * Three things were wrong with the one row on this panel that is the studio's own
 * fault. Its date printed as a raw `2026-08-20` where every other date on the page
 * reads `19 Aug 2026`, because the read model interpolated it into a free-text `note`
 * and the page had nothing to hand `useFormat()`. It was four days overdue against the
 * clock with no mark of it. And the three rows *waiting on the client* each carried a
 * Nudge while the one nobody but the studio can move carried nothing at all — so the
 * panel offered an action for every item except the one it could act on.
 *
 * `due_on` and `days_late` are fields now (`StudioOpenItemSchema`), and the pill is
 * `bad` rather than `need`: `TaskCardFull.taskState` settles the same question the
 * same way — "a late approval is late first" — and every row in this panel is already
 * inside the page's one ochre card, so `need` would be saying what the card says.
 */

import { StudioOpenItemSchema, type StudioClientDetailView } from '@fareast/shared'
import { Link } from '@tanstack/react-router'
import { z } from 'zod'

import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Section, SectionHead } from '../../../components/portal/Layout.js'
import { NeedsBar, NeedsItem, Pill } from '../../../components/portal/Status.js'
import { B, DayText, Muted, Num, Ref, Small } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import { NudgeButton } from './NudgeButton.js'

export const OPEN_ITEMS_ID = 'open-items'

export type OpenItemsSectionProps = {
  readonly detail: StudioClientDetailView
}

/**
 * The open items, parsed rather than read.
 *
 * `useStudioRead` types the answer from the *handler's* return type and not from the
 * schema, so nothing on this path applies a schema default — and `due_on` and
 * `days_late` are declared optional on the wire until `apps/web/src/test/world/*.json`
 * is re-recorded (`StudioOpenItemSchema` says why). Read straight off a recording that
 * predates them they are `undefined`, which is not a value `DayText` accepts and not a
 * value the type admits either. One parse turns both into the `null` the page already
 * has a branch for. `ClientsPage` applies the same guard to the whole book, for the
 * same reason, with the same words.
 */
const OpenItemsSchema = z.array(StudioOpenItemSchema)

export function OpenItemsSection({ detail }: OpenItemsSectionProps) {
  const format = useFormat()
  const openItems = OpenItemsSchema.parse(detail.open_items)
  const theirs = openItems.filter((one) => one.kind === 'blocked_on_client')
  const ours = openItems.filter((one) => one.kind === 'ours_to_quote')
  const withThem = detail.decisions.length + theirs.length

  if (withThem === 0 && ours.length === 0) {
    return (
      <Section id={OPEN_ITEMS_ID}>
        <SectionHead title="Open items" />
        <Empty>Nothing is waiting on them, and we owe them nothing.</Empty>
      </Section>
    )
  }

  return (
    <Section id={OPEN_ITEMS_ID}>
      <SectionHead title="Open items" note={noteFor(withThem, ours.length, format.number)} />

      <Card need>
        <CardBody tight>
          {detail.decisions.map((decision) => (
            <NeedsItem key={decision.ref}>
              <NeedsBar />
              <div>
                <B>{decision.title}</B>
                <div>
                  <Small>
                    <Muted>
                      <Ref value={decision.ref} /> · sent{' '}
                      {format.date(decision.proposed_at, 'short')}
                      {decision.amount === null ? null : (
                        <>
                          {' · '}
                          <Num value={decision.amount} />
                        </>
                      )}
                      {decision.valid_until === null ? null : (
                        <>
                          {' · holds until '}
                          <DayText value={decision.valid_until} nowrap />
                        </>
                      )}
                      . No answer yet.
                    </Muted>
                  </Small>
                </div>
              </div>
              <ButtonRow>
                <NudgeButton client={detail.client} decision={decision} />
              </ButtonRow>
            </NeedsItem>
          ))}

          {theirs.map((item) => (
            <NeedsItem key={item.task_ref ?? item.title}>
              <NeedsBar />
              <div>
                <B>{item.title}</B>
                <div>
                  <Small>
                    <Muted>
                      {item.task_ref === null ? null : (
                        <>
                          <Ref value={item.task_ref} /> ·{' '}
                        </>
                      )}
                      {item.note}
                    </Muted>
                  </Small>
                </div>
              </div>
            </NeedsItem>
          ))}

          {ours.map((item) => (
            <NeedsItem key={item.work_order_ref ?? item.title}>
              <NeedsBar />
              <div>
                <B>{item.title} — ours to quote</B>
                <div>
                  <Small>
                    <Muted>
                      {item.work_order_ref === null ? null : (
                        <>
                          <Ref value={item.work_order_ref} /> ·{' '}
                        </>
                      )}
                      {item.due_on === null ? (
                        item.note
                      ) : (
                        <>
                          {'Quote due '}
                          <DayText value={item.due_on} nowrap />
                          {item.days_late === null ? null : (
                            <>
                              {' · '}
                              <Pill state="bad">
                                {`${format.number(item.days_late)} ${item.days_late === 1 ? 'day' : 'days'} late`}
                              </Pill>
                            </>
                          )}
                        </>
                      )}
                    </Muted>
                  </Small>
                </div>
              </div>
              {item.work_order_ref === null ? null : (
                <ButtonRow>
                  {/*
                    The panel's one row nobody but the studio can move, and until now
                    the only one with no way to move it. `work_orders_price` is the
                    action, and it is the work tab's — this is the affordance that
                    reaches it, on the same page, with the quote in front of you.
                  */}
                  <Button asChild size="sm">
                    <Link
                      params={{ slug: detail.client }}
                      search={{ section: 'work' }}
                      to="/studio/clients/$slug"
                    >
                      Price it
                    </Link>
                  </Button>
                </ButtonRow>
              )}
            </NeedsItem>
          ))}
        </CardBody>
      </Card>
    </Section>
  )
}

/** "Three sit with them; one is ours to send", counted rather than typed. */
function noteFor(withThem: number, ours: number, number: (value: number) => string): string {
  const theirs =
    withThem === 0
      ? 'Nothing sits with them'
      : `${number(withThem)} ${withThem === 1 ? 'sits' : 'sit'} with them`
  const mine =
    ours === 0
      ? 'nothing is ours to send'
      : `${number(ours)} ${ours === 1 ? 'is' : 'are'} ours to send`
  return `${theirs}; ${mine}`
}
